/**
 * Various copies (各種コピー) section of Check Submissions.
 * Table of documents, download all, preview, approval/comment, comment popup.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Submission } from '../types';
import { API_BASE_URL } from '../../../../config';
import { fetchChatFlow, type ChatFlowDocumentFileRound, type ChatFlowDocumentItem, type ChatFlowFormToFillItem } from '../../../../services/chatFlowApi';
import approveIcon from '../../../../assests/icons/approved.png';
import rejectIcon from '../../../../assests/icons/rejected.png';
import folderIcon from '../../../../assests/icons/folder.png';
import downloadAllIcon from '../../../../assests/icons/downloadAll.png';
import commentIcon from '../../../../assests/icons/comment.png';
import DocumentPreview from './DocumentPreview';
import { buildDocumentFileUrl, downloadDocumentFile, safeFileNamePart } from './utils';
import {
  buildResubmissionEmailContent,
  type BuiltResubmissionEmail,
  type ResubmissionTemplatesOverride,
} from './resubmissionManagement/emailTemplates';
import { fillingRoadmapFromChatFlow, type FillingRoadmapState } from './fillingRoadmap';

function getStatusLabel(status: string): string {
  switch (status) {
    case 'submitted':
      return '提出済み';
    case 'send_later':
      return '本社へ後送';
    case 'bring_when_boarding':
      return '乗込時持参';
    case 'skipped':
      return 'スキップ';
    case 'notyet':
    default:
      return '未提出';
  }
}

export interface CheckSubmissionsVariousCopiesProps {
  submission?: Submission | null;
  /** Read-only archive view (no review actions). */
  archiveMode?: boolean;
  /** When set (archive period tab), load this invitation instead of latest. */
  forcedInvitationId?: string | null;
  /** Report loading and total count for parent roadmap label */
  onVariousStateChange?: (state: { loading: boolean; total: number }) => void;
  /** Employee fill-in progress for parent 記入書類 roadmap pill (completed/total). */
  onFillingStateChange?: (state: FillingRoadmapState) => void;
  /** After 各種コピー review, open the 記入書類 tab (step 2 of check submissions). */
  onProceedToFilling?: () => void;
  /** Opens email composer when there are no 記入書類 on the invitation (skip step 2). */
  onRequestResubmissionEmail?: (built: BuiltResubmissionEmail, invitationId: string) => void;
  /** From settings / API; if omitted, built-in defaults in emailTemplates are used */
  resubmissionTemplateOverride?: ResubmissionTemplatesOverride | null;
}

const CheckSubmissionsVariousCopies: React.FC<CheckSubmissionsVariousCopiesProps> = ({
  submission,
  archiveMode = false,
  forcedInvitationId = null,
  onVariousStateChange,
  onFillingStateChange,
  onProceedToFilling,
  onRequestResubmissionEmail,
  resubmissionTemplateOverride,
}) => {
  const [variousDocuments, setVariousDocuments] = useState<ChatFlowDocumentItem[]>([]);
  const [formsToFill, setFormsToFill] = useState<ChatFlowFormToFillItem[]>([]);
  const [invitationId, setInvitationId] = useState('');
  const [loadingVarious, setLoadingVarious] = useState(true);
  const [previewSelection, setPreviewSelection] = useState<{
    doc: ChatFlowDocumentItem;
    round: ChatFlowDocumentFileRound;
  } | null>(null);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitReason, setResubmitReason] = useState('');
  const [commentPopupDoc, setCommentPopupDoc] = useState<ChatFlowDocumentItem | null>(null);

  const loadVariousCopies = useCallback(() => {
    const email = submission?.email?.trim();
    if (!email) {
      setVariousDocuments([]);
      setFormsToFill([]);
      setInvitationId('');
      setLoadingVarious(false);
      onVariousStateChange?.({ loading: false, total: 0 });
      onFillingStateChange?.({ loading: false, completed: 0, total: 0, reviewed: 0 });
      return;
    }
    setLoadingVarious(true);
    onVariousStateChange?.({ loading: true, total: 0 });
    onFillingStateChange?.({ loading: true, completed: 0, total: 0, reviewed: 0 });
    fetchChatFlow(email, 'ja', forcedInvitationId)
      .then((res) => {
        const docs = res?.documents ?? [];
        const forms = res?.formsToFill ?? [];
        setVariousDocuments(docs);
        setFormsToFill(forms);
        setInvitationId(res?.invitationId ?? forcedInvitationId?.trim() ?? '');
        onVariousStateChange?.({ loading: false, total: docs.length });
        onFillingStateChange?.(fillingRoadmapFromChatFlow(res));
      })
      .catch(() => {
        setVariousDocuments([]);
        setFormsToFill([]);
        setInvitationId('');
        onVariousStateChange?.({ loading: false, total: 0 });
        onFillingStateChange?.({ loading: false, completed: 0, total: 0, reviewed: 0 });
      })
      .finally(() => {
        setLoadingVarious(false);
      });
  }, [submission?.email, forcedInvitationId, onVariousStateChange, onFillingStateChange]);

  useEffect(() => {
    loadVariousCopies();
  }, [loadVariousCopies]);

  /** Rows that need admin approve/reject — same scope as backend document-management unfinished check. */
  const submittedDocs = variousDocuments.filter((doc) => doc.status === 'submitted');
  const allSubmittedReviewed =
    submittedDocs.length === 0 ||
    submittedDocs.every(
      (doc) => doc.adminApproved === true || doc.adminApproved === false
    );
  const canSaveAndProceed = !loadingVarious && allSubmittedReviewed;

  const openResubmissionEmail = () => {
    if (!onRequestResubmissionEmail || !invitationId.trim()) return;
    const name = submission?.empName?.trim() ?? '';
    onRequestResubmissionEmail(
      buildResubmissionEmailContent(variousDocuments, name, resubmissionTemplateOverride ?? undefined, formsToFill),
      invitationId.trim()
    );
  };

  const handleSaveAndProceed = () => {
    if (formsToFill.length === 0) {
      openResubmissionEmail();
      return;
    }
    onProceedToFilling?.();
  };

  const handleDownloadAll = async () => {
    const email = submission?.email?.trim();
    if (!email || !invitationId) return;
    const toDownload = variousDocuments.filter(
      (doc) => doc.status === 'submitted' && doc.filePath
    );
    if (toDownload.length === 0) return alert('ダウンロード可能なファイルがありません');

    const empName = submission?.empName?.trim() || email || 'document';
    for (const doc of toDownload) {
      if (!doc.filePath) continue;
      const url = buildDocumentFileUrl(email, invitationId, doc.docCode);
      const docName = doc.nameJa ?? doc.name ?? doc.docCode;
      const ext = doc.filePath.match(/\.[^.]+$/)?.[0] ?? '';
      const fileName = `${safeFileNamePart(empName)}_${safeFileNamePart(docName)}${ext}`;
      try {
        await downloadDocumentFile(url, fileName);
      } catch {
        alert(`${fileName}のダウンロードに失敗しました`);
      }
      await new Promise((r) => setTimeout(r, 300));
    }
  };

  return (
    <>
      <div className="check-submissions__table-wrapper">
        <div className="check-submissions__table-header">
          <div className="check-submissions__table-title">アップロード書類</div>
          <button
            type="button"
            className="check-submissions__download-all-btn"
            onClick={handleDownloadAll}
          >
            <img
              src={downloadAllIcon}
              alt=""
              className="check-submissions__icon-img check-submissions__icon-img--download"
              aria-hidden
            />
            <span>すべてダウンロード</span>
          </button>
        </div>
        <table className="check-submissions__table">
          <thead>
            <tr>
              <th className="check-submissions__th-name">書類名</th>
              <th>ステータス</th>
              <th>ファイル</th>
              <th>承認</th>
              <th>コメント</th>
            </tr>
          </thead>
          <tbody>
            {loadingVarious ? (
              <tr>
                <td colSpan={5} className="check-submissions__td-empty">
                  読み込み中...
                </td>
              </tr>
            ) : variousDocuments.length === 0 ? (
              <tr>
                <td colSpan={5} className="check-submissions__td-empty">
                  書類がありません
                </td>
              </tr>
            ) : (
              variousDocuments.map((doc) => (
                <tr key={doc.docCode}>
                  <td className="check-submissions__td-name">
                    {doc.nameJa ?? doc.name}
                  </td>
                  <td>{getStatusLabel(doc.status)}</td>
                  <td>
                    {(doc.filesByRound ?? []).length > 0 ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 8,
                          alignItems: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        {(doc.filesByRound ?? []).map((round) => (
                          <button
                            key={`${doc.docCode}_${round.submissionRound}`}
                            type="button"
                            className="check-submissions__icon-btn check-submissions__icon-btn--file"
                            aria-label={`ラウンド${round.submissionRound} のファイルをプレビュー`}
                            onClick={() => setPreviewSelection({ doc, round })}
                            disabled={!round.filePath}
                          >
                            <img
                              src={folderIcon}
                              alt=""
                              className="check-submissions__icon-img check-submissions__icon-img--folder"
                              aria-hidden
                            />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span className="check-submissions__file-dash">—</span>
                    )}
                  </td>
                  <td className="check-submissions__td-approval">
                    {doc.adminApproved === true && (
                      <img src={approveIcon} alt="承認済み" className="check-submissions__approval-icon" aria-hidden />
                    )}
                    {doc.adminApproved === false && (
                      <img src={rejectIcon} alt="却下" className="check-submissions__approval-icon" aria-hidden />
                    )}
                  </td>
                  <td className="check-submissions__td-comment">
                    {doc.adminComment != null && doc.adminComment.trim() !== '' && (
                      <button
                        type="button"
                        className="check-submissions__comment-btn"
                        aria-label="コメントを見る"
                        onClick={() => setCommentPopupDoc(doc)}
                      >
                        <img src={commentIcon} alt="" className="check-submissions__comment-icon" aria-hidden />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {!archiveMode && (
        <div className="check-submissions__footer">
          <button
            type="button"
            className="check-submissions__save-btn"
            disabled={!canSaveAndProceed}
            title={
              !canSaveAndProceed && !loadingVarious && submittedDocs.length > 0
                ? '提出済みの書類をすべて承認または却下してください'
                : undefined
            }
            onClick={handleSaveAndProceed}
          >
            保存して次へ進む
          </button>
        </div>
        )}
      </div>

      {commentPopupDoc && (
        <div className="check-submissions__comment-popup" role="dialog" aria-modal="true" aria-labelledby="comment-popup-title">
          <div
            className="check-submissions__comment-popup-backdrop"
            onClick={() => setCommentPopupDoc(null)}
            aria-hidden
          />
          <div className="check-submissions__comment-popup-box">
            <div className="check-submissions__comment-popup-header">
              <h3 id="comment-popup-title" className="check-submissions__comment-popup-title">
                {commentPopupDoc.nameJa ?? commentPopupDoc.name} — コメント
              </h3>
              <button
                type="button"
                className="check-submissions__comment-popup-close"
                aria-label="閉じる"
                onClick={() => setCommentPopupDoc(null)}
              >
                ×
              </button>
            </div>
            <div className="check-submissions__comment-popup-body">
              <p className="check-submissions__comment-popup-text">{commentPopupDoc.adminComment}</p>
            </div>
            <div className="check-submissions__comment-popup-footer">
              <button
                type="button"
                className="check-submissions__comment-popup-ok"
                onClick={() => setCommentPopupDoc(null)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {previewSelection && submission?.email && (
        <DocumentPreview
          doc={previewSelection.doc}
          activeRound={previewSelection.round}
          employeeEmail={submission.email}
          employeeName={submission.empName}
          invitationId={invitationId}
          readOnly={archiveMode}
          allowResubmit={
            archiveMode
              ? false
              : (() => {
            const rounds = previewSelection.doc.filesByRound ?? [];
            const latest = rounds.reduce((max, r) => Math.max(max, r.submissionRound), Number.NEGATIVE_INFINITY);
            return previewSelection.round.submissionRound === latest;
          })()}
          onClose={() => {
            setPreviewSelection(null);
            setShowResubmitModal(false);
            setResubmitReason('');
          }}
          showResubmitModal={showResubmitModal}
          resubmitReason={resubmitReason}
          onResubmitReasonChange={setResubmitReason}
          onOpenResubmitModal={() => setShowResubmitModal(true)}
          onCloseResubmitModal={() => {
            setShowResubmitModal(false);
            setResubmitReason('');
          }}
          onApprove={async () => {
            if (!submission?.email?.trim() || !invitationId?.trim() || !previewSelection?.doc?.docCode) return;
            try {
              const res = await fetch(`${API_BASE_URL}/api/chat-flow/documents/review`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email: submission.email.trim(),
                  invitationId: invitationId.trim(),
                  docCode: previewSelection.doc.docCode,
                  submissionRound: previewSelection.round.submissionRound,
                  adminApproved: true,
                  adminComment: null,
                }),
              });
              if (res.ok) {
                setPreviewSelection(null);
                loadVariousCopies();
              }
            } catch {
              // keep preview open on error
            }
          }}
          onSaveResubmit={async () => {
            if (!submission?.email?.trim() || !invitationId?.trim() || !previewSelection?.doc?.docCode) return;
            try {
              const res = await fetch(`${API_BASE_URL}/api/chat-flow/documents/review`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email: submission.email.trim(),
                  invitationId: invitationId.trim(),
                  docCode: previewSelection.doc.docCode,
                  submissionRound: previewSelection.round.submissionRound,
                  adminApproved: false,
                  adminComment: resubmitReason.trim() || null,
                }),
              });
              if (res.ok) {
                setShowResubmitModal(false);
                setResubmitReason('');
                setPreviewSelection(null);
                loadVariousCopies();
              }
            } catch {
              // keep modal open on error
            }
          }}
        />
      )}
    </>
  );
};

export default CheckSubmissionsVariousCopies;
